import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Field, FieldArray, reduxForm, getFormValues } from 'redux-form';
import Button from '../../../../../components/Button';
import Dialog from '../../../../../components/Dialog';
import Table from '../../../../../components/lists/Table';
import FormFieldText from '../../../../../components/forms/FormFieldText';
import FormFieldSearch from '../../../../../components/forms/FormFieldSearch';
import FormFieldSelect from '../../../../../components/forms/FormFieldSelect';
import { makeJsonSchemaFormValidator } from '../../../../../utils/errors';
import {
  daysOfMonthOptions,
  daysOfWeekOptions,
  hoursOptions,
  INTERVAL_DAY,
  INTERVAL_HOUR,
  INTERVAL_MONTH,
  INTERVAL_WEEK,
  INTERVAL_YEAR,
  intervalOptions,
  monthsOptions,
  cronToParams,
  paramsToCron,
  timeRangeOptions,
} from './helpers';
import { getMinutesScheduleError } from './scheduleMailConfigErrors';
import {
  callMethod,
  // ...
} from '../../../../../common/utilsClient/ddp/actions';
import {
  apiZedocSupersetGetMailReportsByUserAndDashboardWise,
  apiZedocSupersetSaveMailReportByUserAndDashboardWise,
  apiZedocSupersetGetSingleMailReportByReportIdUserAndDashboardWise,
  apiZedocSupersetDeleteMailReportByReportIdUserAndDashboardWise,
  apiZedocSupersetGetMailReportLogsByUserAndDashboardWise,
} from '../../../../../common/api/zedoc';
import { notifyError } from '../../../../../utils/notify';
import FormFieldSwitch from '../../../../../components/forms/FormFieldSwitch';
import { toIntlDateTimeFormat } from '@zedoc/date';
import MailSchedulerFields from '../../../../../screens/SettingsProject_v2/components/MailSchedulerFields';
import MailRecipientsFields from '../../../../../screens/SettingsProject_v2/components/MailRecipientsFields';

// ======================
// Helper Components
// ======================

const FilterRangeInput = ({ input, label }) => {
  const from = input.value[0]?.from || '';
  const to = input.value[0]?.to || '';

  const handleFromChange = (e) => {
    const newFrom = e.target.value;
    input.onChange([
      {
        ...input.value[0],
        from: newFrom,
      },
    ]);
  };

  const handleToChange = (e) => {
    const newTo = e.target.value;
    input.onChange([
      {
        ...input.value[0],
        to: newTo,
      },
    ]);
  };

  return (
    <div className="flex flex-col">
      {label && <label className="mb-1 font-medium">{label}</label>}
      <div className="flex items-center border rounded overflow-hidden">
        <input
          type="number"
          value={from}
          onChange={handleFromChange}
          className="flex-1 p-2 border-none outline-none"
          placeholder="From"
        />
        <span className="px-2 text-gray-400">/</span>
        <input
          type="number"
          value={to}
          onChange={handleToChange}
          className="flex-1 p-2 border-none outline-none"
          placeholder="To"
        />
      </div>
    </div>
  );
};

const CustomTextSelectInput = ({ input, label, options }) => {
  // Ensure input.value is either a string or first element of an array
  const inputValue = Array.isArray(input.value)
    ? input.value[0] || ''
    : input.value || '';

  const handleChange = (e) => {
    const newValue = e.target.value;
    // Wrap newValue in an array, since filter_value is an array in your form
    input.onChange([newValue]);
  };

  return (
    <div className="flex flex-col">
      {label && <label className="mb-1 font-medium">{label}</label>}
      <input
        value={inputValue}
        onChange={handleChange}
        list="custom-options"
        className="p-2 border rounded"
        placeholder="Select or type..."
      />
      <datalist id="custom-options">
        {options.map((opt, idx) => (
          <option key={idx} value={opt} />
        ))}
      </datalist>
    </div>
  );
};

const form = 'screens_projects_dashboard_manageEmailReports';

const EmailReportProperties = ({ member }) => {
  const { t } = useTranslation();
  const formValues = useSelector((state) => getFormValues(form)(state));
  const idx = member.match(/\[(\d+)\]/)[1];
  const { interval } = formValues.emailReports[idx];

  return (
    <div className="stack-6">
      <Field
        data-testid="field-is-active"
        component={FormFieldSwitch}
        name={`${member}.active`}
        label={t('states:active')}
        required
      />
      <Field
        data-testid="field-name"
        component={FormFieldText}
        name={`${member}.name`}
        type="text"
        label={t('forms:name.label')}
        required
      />
      <Field
        data-testid="field-description"
        component={FormFieldText}
        name={`${member}.description`}
        type="freeText"
        label={t('forms:description.label')}
        required
      />
      <MailSchedulerFields
        member={member}
        getIntervalValue={() => interval}
        showTimezone
      />
      <MailRecipientsFields member={member} />
    </div>
  );
};

EmailReportProperties.propTypes = {
  member: PropTypes.string.isRequired,
};

const EmailReportsList = ({ fields, embeddedDashboardId, golobalFilters }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const dispatch = useDispatch();
  const formValues = useSelector((state) => getFormValues(form)(state));
  const [expandedIndex, setExpandedIndex] = useState(null);

  // State for the "Logs" dialog
  const [logsOpen, setLogsOpen] = useState(false);
  const [logsData, setLogsData] = useState({});

  // State for the "Logs" dialog
  const [filtersOpen, setfilterOpen] = useState(false);
  const [filtersData, setfilterData] = useState({});
  const [selectedMember, setSelectedMember] = useState(null);

  const selectedFilterCount = React.useMemo(() => {
    if (!selectedMember || !formValues) return 0;
    // selectedMember is a string like "emailReports[0]"
    const match = selectedMember.match(/\[(\d+)\]/);
    if (!match) return 0;
    const reportIndex = parseInt(match[1], 10);
    const report = formValues.emailReports?.[reportIndex];
    if (!report || !Array.isArray(report.filters)) return 0;

    // Create a Set of filter IDs that have non-empty filter values
    const distinctIds = new Set(
      report.filters
        .filter((filter) => {
          if (
            Array.isArray(filter.filter_value) &&
            filter.filter_value.length > 0
          ) {
            if (typeof filter.filter_value[0] === 'object') {
              if (filter.filter_value[0] !== null) {
                const hasNonEmptyValue = Object.values(
                  filter.filter_value[0],
                ).some((val) => typeof val === 'string' && val.trim() !== '');
                return hasNonEmptyValue;
              }
              return false;
            } else {
              return true; // first element is not an object
            }
          }
          return false;
        })
        .map((filter) => filter.filter_id),
    );

    return distinctIds.size;
  }, [selectedMember, formValues]);

  // Add new email-report row
  const handleAdd = () => {
    fields.push({ active: true, filters: golobalFilters });
    setExpandedIndex(fields.length); // newly added item index
  };

  const handleCancelItem = (index) => {
    const itemValues = formValues?.emailReports?.[index];
    if (!itemValues?.reportId) {
      fields.remove(index);
    }
    setExpandedIndex(null);
  };

  // Show logs
  const showLogs = (index) => {
    const itemValues = formValues?.emailReports?.[index];
    if (!itemValues?.reportId) {
      console.warn('No values to edit for item at index', index);
      return;
    }

    dispatch(
      callMethod(apiZedocSupersetGetMailReportLogsByUserAndDashboardWise, {
        embeddedDashboardId,
        reportId: itemValues.reportId,
      }),
    )
      .then((data) => {
        const logDatas = {
          logs: data || [],
          name: itemValues.name,
          crontab_humanized: itemValues.crontab_humanized,
        };
        setLogsData(logDatas || { logs: [] });
        setLogsOpen(true);
      })
      .catch(notifyError());
  };

  // Close logs
  const closeLogs = () => {
    setLogsData([]);
    setLogsOpen(false);
  };

  // Show logs
  const showFilters = (index, member) => {
    const itemValues = formValues?.emailReports?.[index];
    setfilterData(itemValues || { filters: [] });
    setSelectedMember(member);
    setfilterOpen(true);
  };

  // Close logs
  const closeFilters = () => {
    setfilterData(null);
    setfilterOpen(false);
    setSelectedMember(null);
  };

  // Edit existing item
  const handleEdit = (index) => {
    const itemValues = formValues?.emailReports?.[index];
    if (!itemValues?.reportId) {
      console.warn('No values to edit for item at index', index);
      return;
    }

    dispatch(
      callMethod(
        apiZedocSupersetGetSingleMailReportByReportIdUserAndDashboardWise,
        {
          embeddedDashboardId,
          reportId: itemValues.reportId,
        },
      ),
    )
      .then((data) => {
        const reportFilters = data?.extra?.dashboard?.dataMask || {};

        const transformedFilters = golobalFilters.map((filter) => {
          const newFilter = { ...filter };
          const reportFilter = reportFilters[filter.filter_id];

          if (reportFilter) {
            if (
              filter.filter_type === 'filter_select' &&
              reportFilter.extraFormData?.filters?.[0]?.val
            ) {
              newFilter.filter_value =
                reportFilter.extraFormData.filters[0].val || [];
            } else if (
              filter.filter_type === 'filter_range' &&
              reportFilter.extraFormData?.filters
            ) {
              const filters = reportFilter.extraFormData.filters;
              const fromFilter = filters.find((f) => f.op === '>=');
              const toFilter = filters.find((f) => f.op === '<=');

              newFilter.filter_value = [
                {
                  from: fromFilter?.val ?? '',
                  to: toFilter?.val ?? '',
                },
              ];
            } else if (
              filter.filter_type === 'filter_time' &&
              reportFilter.extraFormData?.time_range
            ) {
              newFilter.filter_value =
                [reportFilter.extraFormData.time_range] || [];
            } else {
              // default empty for other types or if not found
              newFilter.filter_value = [];
            }
          } else {
            newFilter.filter_value = [];
          }

          return newFilter;
        });

        formValues.emailReports[index] = {
          ...data,
          ...cronToParams(data?.crontab),
          filters: transformedFilters,
        };
        setExpandedIndex(index);
      })
      .catch(notifyError());
  };

  const handleRemove = (index) => {
    const itemValues = formValues?.emailReports?.[index];
    if (!itemValues?.reportId) {
      console.warn('No values to remove for item at index', index);
      return;
    }

    const confirmed = window.confirm(
      'Are you sure you want to DELETE this report?',
    );
    if (!confirmed) {
      return;
    }

    dispatch(
      callMethod(
        apiZedocSupersetDeleteMailReportByReportIdUserAndDashboardWise,
        {
          embeddedDashboardId,
          reportId: itemValues.reportId,
        },
      ),
    )
      .then((data) => {
        if (data == true) {
          fields.remove(index);
          if (expandedIndex === index) setExpandedIndex(null);
        }
      })
      .catch(notifyError());
  };

  // Save an individual item.
  const handleSaveItem = (index) => {
    const itemValues = formValues?.emailReports?.[index];
    if (!itemValues) {
      console.warn('No values to save for item at index', index);
      return;
    }

    const minutesError = getMinutesScheduleError(itemValues.minutes, {
      wrapAround: itemValues.interval === INTERVAL_HOUR,
    });
    if (minutesError) {
      notifyError()(minutesError);
      return;
    }

    const newReportFilters = itemValues.filters.reduce((acc, filter) => {
      const entry = { id: filter.filter_id };

      if (
        filter.filter_type === 'filter_select' &&
        Array.isArray(filter.filter_value) &&
        filter.filter_value.length > 0
      ) {
        entry.extraFormData = {
          filters: [
            {
              col: filter.filter_column,
              op: 'IN',
              val: filter.filter_value,
            },
          ],
        };
        entry.filterState = {
          value: filter.filter_value,
        };
        acc[filter.filter_id] = entry;
      } else if (
        filter.filter_type === 'filter_range' &&
        Array.isArray(filter.filter_value) &&
        filter.filter_value.length == 1 &&
        typeof filter.filter_value[0] === 'object'
      ) {
        const from = filter.filter_value[0].from;
        const to = filter.filter_value[0].to;

        const fromValue =
          from !== undefined && from !== null && from !== ''
            ? Number(from)
            : undefined;
        const toValue =
          to !== undefined && to !== null && to !== '' ? Number(to) : undefined;

        const filters = [];
        if (fromValue !== undefined && !Number.isNaN(fromValue)) {
          filters.push({
            col: filter.filter_column,
            op: '>=',
            val: fromValue,
          });
        }

        if (toValue !== undefined && !Number.isNaN(toValue)) {
          filters.push({
            col: filter.filter_column,
            op: '<=',
            val: toValue,
          });
        }

        if (filters.length > 0) {
          entry.extraFormData = {
            filters,
          };
        }

        const rangeValues = [];
        if (fromValue !== undefined && !Number.isNaN(fromValue)) {
          rangeValues.push(fromValue);
        }
        if (toValue !== undefined && !Number.isNaN(toValue)) {
          rangeValues.push(toValue);
        }

        if (rangeValues.length > 0) {
          entry.filterState = {
            value: rangeValues,
          };
        }

        acc[filter.filter_id] = entry;
      } else if (
        filter.filter_type === 'filter_time' &&
        Array.isArray(filter.filter_value) &&
        filter.filter_value.length === 1 &&
        filter.filter_value[0].trim() !== ''
      ) {
        entry.extraFormData = {
          time_range: filter.filter_value[0].trim(),
        };
        entry.filterState = {
          value: filter.filter_value[0].trim(),
        };
        acc[filter.filter_id] = entry;
      }

      return acc;
    }, {});

    const params = {
      embeddedDashboardId,
      report: {
        ...(itemValues.reportId && { reportId: itemValues.reportId }),
        active: itemValues.active,
        name: itemValues.name,
        description: itemValues.description,
        crontab: paramsToCron({
          interval: itemValues.interval,
          minutes: itemValues.minutes,
          hours: itemValues.hours,
          daysOfMonth: itemValues.daysOfMonth,
          months: itemValues.months,
          daysOfWeek: itemValues.daysOfWeek,
        }),
        timezone: itemValues.timezone,
        recipient: {
          target: itemValues?.recipient?.target?.join(';') || '',
          ccTarget: itemValues?.recipient?.ccTarget?.join(';') || '',
          bccTarget: itemValues?.recipient?.bccTarget?.join(';') || '',
        },
        extra: { dashboard: { dataMask: newReportFilters } },
      },
    };

    dispatch(
      callMethod(apiZedocSupersetSaveMailReportByUserAndDashboardWise, params),
    )
      .then((data) => {
        formValues.emailReports[index] = {
          ...data,
          ...cronToParams(data?.crontab),
        };

        setExpandedIndex(null);
      })
      .catch(notifyError());
  };

  const logsColumns = [
    {
      title: 'State',
      key: 'state',
    },
    {
      title: 'Scheduled',
      key: 'scheduled_dttm',
      render: (date) =>
        toIntlDateTimeFormat(language, new Date(date), {
          dateStyle: 'short',
          timeStyle: 'short',
        }),
    },
    {
      title: 'Error',
      key: 'error_message',
      render: (error) => error || '-',
    },
  ];

  return (
    <div className="stack-6">
      <div className="cluster-2 items-center justify-between">
        <div>
          <p className="font-medium">{t('emailReports')}</p>
          <p>{t('totalWithCount', { count: fields.length })}</p>
        </div>
        <Button type="primary" onClick={handleAdd}>
          {t('add')}
        </Button>
      </div>
      {fields?.length > 0 && <hr className="border-divider" />}
      <div className="stack-4">
        {fields.map((member, index) => {
          const isExpanded = expandedIndex === index;
          return (
            <div
              key={member}
              className="stack-4 border border-divider p-4 rounded-md"
            >
              {/* Title / Buttons row */}
              <div className="cluster-4 items-center justify-between">
                <div className="cluster-2 items-center">
                  <div
                    className={`w-3 h-3 rounded-full ${
                      formValues?.emailReports?.[index]?.active
                        ? 'bg-success'
                        : 'bg-danger'
                    }`}
                  />
                  <span className="font-medium">
                    {formValues?.emailReports?.[index]?.name || index + 1}
                  </span>
                </div>

                <div className="cluster-2">
                  {formValues?.emailReports?.[index]?.reportId && (
                    <Button onClick={() => showLogs(index)}>{t('logs')}</Button>
                  )}

                  {isExpanded && (
                    <Button onClick={() => showFilters(index, member)}>
                      {t('filters')} (
                      {formValues?.emailReports?.[index]?.filters
                        ? new Set(
                            formValues.emailReports[index].filters
                              .filter(
                                (filter) =>
                                  Array.isArray(filter.filter_value) &&
                                  filter.filter_value.length > 0,
                              )
                              .map((filter) => filter.filter_id),
                          ).size
                        : 0}
                      )
                    </Button>
                  )}

                  {formValues?.emailReports?.[index]?.reportId && (
                    <Button type="danger" onClick={() => handleRemove(index)}>
                      {t('remove')}
                    </Button>
                  )}

                  {isExpanded && (
                    <Button
                      type="ghost"
                      onClick={() => handleCancelItem(index)}
                    >
                      {t('cancel')}
                    </Button>
                  )}

                  {!isExpanded && (
                    <Button type="primary" onClick={() => handleEdit(index)}>
                      {t('edit')}
                    </Button>
                  )}

                  {isExpanded && (
                    <Button
                      type="primary"
                      onClick={() => handleSaveItem(index)}
                    >
                      {t('save')}
                    </Button>
                  )}
                </div>
              </div>

              {isExpanded && (
                <>
                  <hr className="border-divider" />
                  <EmailReportProperties member={member} />
                </>
              )}
            </div>
          );
        })}
      </div>
      {/* ====================================== */}
      {/* Logs Dialog (conditionally displayed) */}
      <Dialog
        title={logsData.name}
        size="xl"
        visible={logsOpen}
        onCancel={closeLogs}
      >
        <div className="stack-6">
          <span className="font-medium">{logsData.crontab_humanized}</span>

          {logsData.logs && logsData.logs.length > 0 ? (
            <Table dataSource={logsData.logs} columns={logsColumns} />
          ) : (
            <p>No log data found.</p>
          )}
        </div>
      </Dialog>
      {/* Filters Dialog (conditionally displayed) */}
      <Dialog
        title={`${t('filters')} (${selectedFilterCount})`}
        visible={filtersOpen}
        onCancel={closeFilters}
      >
        <div className="stack-6">
          {filtersData &&
          filtersData?.filters &&
          filtersData?.filters.length > 0 ? (
            filtersData.filters.map((filter, idx) => (
              <div key={filter.filter_id} className="stack-4">
                {filter.filter_type === 'filter_select' && (
                  <Field
                    data-testid={`${filter.filter_id}`}
                    component={FormFieldSearch}
                    name={`${selectedMember}.filters[${idx}].filter_value`}
                    label={filter.filter_name + ' [Exact value]'}
                    mode="tags"
                    options={
                      filter.filter_options && filter.filter_options.length > 0
                        ? filter.filter_options
                        : undefined
                    }
                  />
                )}

                {filter.filter_type === 'filter_range' && (
                  <div>
                    <div className="mt-1">
                      <Field
                        data-testid={`${filter.filter_id}`}
                        name={`${selectedMember}.filters[${idx}].filter_value`}
                        label={filter.filter_name + ' [Numerical range]'}
                        component={FilterRangeInput}
                      />
                    </div>
                  </div>
                )}

                {filter.filter_type === 'filter_time' && (
                  <Field
                    data-testid={`${filter.filter_id}`}
                    component={CustomTextSelectInput}
                    name={`${selectedMember}.filters[${idx}].filter_value`}
                    label={filter.filter_name + ' [Time range]'}
                    options={timeRangeOptions.map((opt) => opt.value)}
                  />
                )}
              </div>
            ))
          ) : (
            <p>No filter data found.</p>
          )}
        </div>
      </Dialog>
    </div>
  );
};

EmailReportsList.propTypes = {
  fields: PropTypes.object.isRequired,
};

const ManageEmailReportsForm = reduxForm({
  validate: makeJsonSchemaFormValidator({
    type: 'object',
    required: ['emailReports'],
    properties: {
      emailReports: {
        type: 'array',
        items: {
          type: 'object',
          required: [
            'name',
            'description',
            'timezone',
            'interval',
            'months',
            'daysOfMonth',
            'daysOfWeek',
            'hours',
            'minutes',
          ],
          properties: {
            name: {
              type: 'string',
              title: 'Name',
            },
            description: {
              type: 'string',
              title: 'Description',
            },
            timezone: {
              type: 'string',
              title: 'Timezone',
            },
            interval: {
              type: 'string',
              title: 'Every',
            },
            months: {
              type: 'array',
              title: 'Months',
            },
            daysOfMonth: {
              type: 'array',
              title: 'Days of Month',
            },
            daysOfWeek: {
              type: 'array',
              title: 'Days of Week',
            },
            hours: {
              type: 'array',
              title: 'Hours',
            },
            minutes: {
              type: 'array',
              title: 'Minutes',
            },
            emails: {
              type: 'array',
              title: 'Email Recipients',
            },
            ccEmails: {
              type: 'array',
              title: 'Email CC Recipients',
            },
            bccEmails: {
              type: 'array',
              title: 'Email BCC Recipients',
            },
          },
        },
        minItems: 1,
      },
      embeddedDashboardId: {
        type: 'string',
      },
      golobalFilters: {
        type: 'array',
      },
    },
  }),
  form, // use the constant defined above
})(({ error, embeddedDashboardId, golobalFilters }) => {
  const { t } = useTranslation();

  return (
    <div>
      {error && <span>{error}</span>}
      <FieldArray
        name="emailReports"
        label={t('emailReports')}
        component={EmailReportsList}
        embeddedDashboardId={embeddedDashboardId}
        golobalFilters={golobalFilters}
      />
    </div>
  );
});

const ManageEmailReports = ({ embeddedDashboardId }) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const dispatch = useDispatch();
  const handleOnOpenDialog = () => {
    dispatch(
      callMethod(apiZedocSupersetGetMailReportsByUserAndDashboardWise, {
        embeddedDashboardId,
      }),
    )
      .then((data) => {
        const reportsWithFilters = (data?.reports || []).map((report) => {
          return {
            ...report,
          };
        });
        setEmailReports(reportsWithFilters || []);
        setGolobalFilters(data?.filters);
        setIsVisible(true);
      })
      .catch(notifyError());
  };
  const handleOnCloseDialog = () => setIsVisible(false);

  // TODO: Replace "emailReports" with collection items coming from backend
  const [emailReports, setEmailReports] = useState([]);
  const [golobalFilters, setGolobalFilters] = useState([]);
  const initialValues = {
    emailReports: emailReports.length ? emailReports : [],
  };

  return (
    <>
      <Button onClick={handleOnOpenDialog}>{t('manageEmailReports')}</Button>
      <Dialog
        title={t('manageEmailReports')}
        size="large"
        visible={isVisible}
        onCancel={handleOnCloseDialog}
        footer={null}
      >
        <ManageEmailReportsForm
          form={form}
          initialValues={initialValues}
          embeddedDashboardId={embeddedDashboardId}
          golobalFilters={golobalFilters}
        />
      </Dialog>
    </>
  );
};

export default ManageEmailReports;
